import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TransactionCategory } from '@shipstation/financial-services-ui-components';

import { WwexRegistrationModal } from '../../../../../components/wwex-registration-modal';
import type { WwexFormData } from '../../../../../components/wwex-registration-modal';
import { useResolveConnectedProviderId } from '../../../../../hooks/freight/use-resolve-connected-provider-id';
import { refreshSellerFreightProviders } from '../../../../../store/seller/seller.actions';
import { PaymentAndSubscriptionMegaContextProvider } from '../../../../../pages/settings/payment-and-subscription/context';
import FsuiContextProvider from '../../../../../financial-services-ui/fsui-context-provider';
import { FinancialServicesUIProviderWrapper } from '../../../../../financial-services-ui/financial-services-ui-provider-wrapper';
import Global from '../../../../../store/global-selectors';

type WwexRegistrationContainerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (sellerFreightProviderId: number | null) => void;
  onManageClick?: () => Promise<boolean>;
};

export const WwexRegistrationContainer = ({ isOpen, onClose, onSuccess, onManageClick }: WwexRegistrationContainerProps) => {
  const dispatch = useDispatch();
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const resolveConnectedProviderId = useResolveConnectedProviderId();

  const handleSuccess = useCallback(
    async (_data: WwexFormData, sellerFreightProviderId: number | null) => {
      // Retry the booking flow with the newly-registered account (requote, not re-book).
      // The modal itself closes on success, so we don't call onClose() here too.
      if (sellerFreightProviderId) {
        dispatch(refreshSellerFreightProviders());
        onSuccess(sellerFreightProviderId);
        return;
      }

      // WWEX registration didn't hand back an id directly, so resolve it the same way the
      // CUI connect flow does — onSuccess always resolves with a real id instead of the
      // caller having to wait for a separate refresh to land later.
      onSuccess(await resolveConnectedProviderId());
    },
    [dispatch, onSuccess, resolveConnectedProviderId],
  );

  if (!isOpen) {
    return null;
  }

  return (
    <PaymentAndSubscriptionMegaContextProvider>
      <FsuiContextProvider currencyCode={sellerCurrencyCode} transactionCategory={TransactionCategory.AddFunds}>
        <FinancialServicesUIProviderWrapper>
          <WwexRegistrationModal isOpen={isOpen} onClose={onClose} onSuccess={handleSuccess} onManageClick={onManageClick} />
        </FinancialServicesUIProviderWrapper>
      </FsuiContextProvider>
    </PaymentAndSubscriptionMegaContextProvider>
  );
};
